import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { useLandingTheme } from '../hooks/useLandingTheme';
import { getSubscriptions, SubscriptionPlan } from 'services/subscriptions/subscriptionService';

type BillingCycle = 'monthly' | 'yearly';

/* ── Helper: find an action's limit in a plan ── */
const getActionLimit = (
  plan: SubscriptionPlan,
  actionName: string
): { limit: number; unit: string } | null => {
  const action = plan.actions.find((a) => a.action_details?.name === actionName);
  if (!action) return null;
  return { limit: action.action_limit, unit: action.limit_unit || 'day' };
};

/* ── Helper: compute social proof boost (likes_auto_like_posts) ── */
const getSocialProofBoost = (plan: SubscriptionPlan): { limit: number; unit: string } | null => {
  return getActionLimit(plan, 'likes_auto_like_posts');
};

/* ── Helper: support level → response time ── */
const supportLevelToTime = (level?: string): string => {
  switch (level) {
    case 'human_priority':
      return '24h';
    case 'priority':
      return '48h';
    default:
      return '72h';
  }
};

/* ── Comparison table cell types ── */
interface ComparisonCell {
  value?: string;
  period?: string;
  isCheck?: boolean;
  isEmpty?: boolean;
  isIdealFor?: boolean;
  translationKey?: string;
}

interface ComparisonRow {
  labelKey: string;
  cells: ComparisonCell[];
  tall?: boolean;
}

/* ── Map plan name → i18n key segments ── */
const planKeyMap: Record<string, string> = {
  Starter: 'starter',
  Pro: 'pro',
  Elite: 'elite',
};

const idealForKeys: Record<string, string> = {
  Starter: 'landing.pricing.comparison.idealForStarter',
  Pro: 'landing.pricing.comparison.idealForPro',
  Elite: 'landing.pricing.comparison.idealForElite',
};

/* ── Build comparison rows from API plans ── */
const buildComparisonRows = (plans: SubscriptionPlan[]): ComparisonRow[] => {
  const cellFor = (
    plan: SubscriptionPlan,
    actionName: string
  ): ComparisonCell => {
    const result = getActionLimit(plan, actionName);
    if (!result) return { isEmpty: true };
    return { value: String(result.limit), period: `/${result.unit}` };
  };

  const rows: ComparisonRow[] = [
    {
      labelKey: 'landing.pricing.comparison.socialProofBoost',
      cells: plans.map((p) => {
        const result = getSocialProofBoost(p);
        if (!result) return { isEmpty: true };
        return { value: String(result.limit), period: `/${result.unit}` };
      }),
    },
    {
      labelKey: 'landing.pricing.comparison.aiSmartReplies',
      cells: plans.map((p) => cellFor(p, 'comments_keyword_smart_reply')),
    },
    {
      labelKey: 'landing.pricing.comparison.automatedDM',
      cells: plans.map((p) => cellFor(p, 'messages_auto_dm_followers')),
    },
    {
      labelKey: 'landing.pricing.comparison.smartFollowUnfollow',
      cells: plans.map((p) => {
        const follow = getActionLimit(p, 'following_auto_follow_users');
        if (!follow) return { isEmpty: true };
        return { isCheck: true };
      }),
    },
    {
      labelKey: 'landing.pricing.comparison.contentAmplification',
      cells: plans.map((p) => {
        const result = getActionLimit(p, 'repost_posts');
        if (!result) return { isEmpty: true };
        return { value: String(result.limit), period: `/${result.unit}` };
      }),
    },
    {
      labelKey: 'landing.pricing.comparison.connectedAccounts',
      cells: plans.map((p) => ({ value: String(p.number_of_accounts) })),
    },
    {
      labelKey: 'landing.pricing.comparison.support',
      cells: plans.map((p) => ({ value: supportLevelToTime(p.support_level) })),
    },
    {
      labelKey: 'landing.pricing.comparison.idealFor',
      cells: plans.map((p) => ({
        isIdealFor: true,
        translationKey: idealForKeys[p.subscription_name] || '',
      })),
      tall: true,
    },
  ];

  return rows;
};

const PricingSection: React.FC = () => {
  const { t } = useTranslation();
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('yearly');
  const isDark = useLandingTheme();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchPlans = async () => {
      try {
        const data = await getSubscriptions();
        if (!cancelled) {
          // Filter out Trial (tier 0), keep only tier >= 1, sorted by tier
          const filtered = data.plans
            .filter((p) => (p.tier ?? 0) >= 1)
            .sort((a, b) => (a.tier ?? 0) - (b.tier ?? 0));
          setPlans(filtered);
        }
      } catch {
        // On error, plans remain empty — fallback handled in UI
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchPlans();
    return () => { cancelled = true; };
  }, []);

  const getFeatures = (key: string): string[] => {
    const features = t(key, { returnObjects: true });
    return Array.isArray(features) ? features : [];
  };

  const borderColor = isDark ? '#1D293D' : '#F1F5F9';

  const comparisonRows = plans.length > 0 ? buildComparisonRows(plans) : [];

  const renderCell = (cell: ComparisonCell) => {
    if (cell.isEmpty) return null;

    if (cell.isCheck) {
      return <Check size={20} style={{ color: isDark ? '#51A2FF' : '#2B7FFF' }} />;
    }

    if (cell.isIdealFor && cell.translationKey) {
      return (
        <span
          className="font-geist whitespace-pre-line"
          style={{
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '20px',
            color: isDark ? '#CAD5E2' : '#45556C',
          }}
        >
          {t(cell.translationKey)}
        </span>
      );
    }

    return (
      <span className="flex items-center" style={{ gap: '2px' }}>
        <span
          className="font-geist"
          style={{
            fontWeight: 600,
            fontSize: '14px',
            lineHeight: '20px',
            color: isDark ? '#CAD5E2' : '#45556C',
          }}
        >
          {cell.value}
        </span>
        {cell.period && (
          <span
            className="font-geist"
            style={{
              fontWeight: 400,
              fontSize: '12px',
              lineHeight: '16px',
              color: '#90A1B9',
            }}
          >
            {cell.period}
          </span>
        )}
      </span>
    );
  };

  /* ── Price display helpers ── */
  const getDisplayPrice = (plan: SubscriptionPlan): string => {
    if (billingCycle === 'yearly') {
      return `${plan.price.amount_4weeks_annual}€`;
    }
    return `${plan.price.amount_4weeks}€`;
  };

  const getOriginalPrice = (plan: SubscriptionPlan): string | null => {
    if (billingCycle === 'yearly' && plan.price.amount_4weeks_annual < plan.price.amount_4weeks) {
      return `${plan.price.amount_4weeks}€`;
    }
    return null;
  };

  const isHighlighted = (plan: SubscriptionPlan): boolean => plan.tier === 2;

  /* ── Loading skeleton ── */
  if (loading) {
    return (
      <section
        id="pricing"
        className="py-16 md:py-32"
        style={{ background: isDark ? '#0F172B' : '#FFFFFF' }}
      >
        <div
          className="max-w-[1200px] mx-auto px-6 lg:px-0 flex flex-col items-center"
          style={{ gap: '48px' }}
        >
          <div className="w-full flex flex-col items-center" style={{ gap: '24px' }}>
            <div
              className="rounded-lg animate-pulse w-[200px] md:w-[300px]"
              style={{
                height: '48px',
                background: isDark ? '#1D293D' : '#F1F5F9',
              }}
            />
            <div
              className="rounded-lg animate-pulse w-[280px] md:w-[400px]"
              style={{
                height: '28px',
                background: isDark ? '#1D293D' : '#F1F5F9',
              }}
            />
          </div>
          <div className="w-full grid grid-cols-1 md:grid-cols-3" style={{ gap: '24px' }}>
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="rounded-2xl animate-pulse"
                style={{
                  height: '320px',
                  background: isDark ? '#1D293D' : '#F1F5F9',
                }}
              />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (plans.length === 0) return null;

  return (
    <section
      id="pricing"
      className="py-16 md:py-32"
      style={{ background: isDark ? '#0F172B' : '#FFFFFF' }}
    >
      <div
        className="max-w-[1200px] mx-auto px-6 lg:px-0 flex flex-col items-start"
        style={{ gap: '48px' }}
      >
        {/* Header */}
        <div className="w-full flex flex-col items-start" style={{ gap: '16px' }}>
          <h2
            className="font-geist w-full text-center text-[28px] leading-[34px] md:text-[48px] md:leading-[48px]"
            style={{
              fontWeight: 600,
              color: isDark ? '#E2E8F0' : '#314158',
            }}
          >
            {t('landing.pricing.title')}
          </h2>
          <p
            className="font-geist w-full text-center whitespace-pre-line text-base md:text-xl"
            style={{
              fontWeight: 400,
              lineHeight: '28px',
              color: isDark ? '#E2E8F0' : '#314158',
            }}
          >
            {t('landing.pricing.subtitle')}
          </p>
        </div>

        {/* Container: toggle + cards + table */}
        <div className="w-full flex flex-col items-center" style={{ gap: '40px' }}>
          {/* Billing Toggle */}
          <div
            className="inline-flex items-end"
            style={{
              padding: '4px',
              gap: '2px',
              background: isDark ? '#314158' : '#F1F5F9',
              borderRadius: '12px',
            }}
          >
            <button
              onClick={() => setBillingCycle('monthly')}
              className="font-geist flex items-center justify-center"
              style={{
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px',
                padding: '8px 12px',
                borderRadius: '8px',
                color: billingCycle === 'monthly'
                  ? isDark ? '#0F172B' : '#FFFFFF'
                  : isDark ? '#E2E8F0' : '#314158',
                background: billingCycle === 'monthly'
                  ? isDark ? '#E2E8F0' : '#314158'
                  : 'transparent',
              }}
            >
              {t('landing.pricing.monthlyBilled')}
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className="font-geist flex items-center justify-center"
              style={{
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px',
                padding: '8px 12px',
                borderRadius: '8px',
                color: billingCycle === 'yearly'
                  ? isDark ? '#0F172B' : '#FFFFFF'
                  : isDark ? '#E2E8F0' : '#314158',
                background: billingCycle === 'yearly'
                  ? isDark ? '#E2E8F0' : '#314158'
                  : 'transparent',
              }}
            >
              {t('landing.pricing.yearlyBilled')}
            </button>
          </div>

          {/* Pricing Cards */}
          <div className="w-full grid grid-cols-1 md:grid-cols-3" style={{ gap: '24px' }}>
            {plans.map((plan) => {
              const key = planKeyMap[plan.subscription_name] || plan.subscription_name.toLowerCase();
              const features = getFeatures(`landing.pricing.${key}.features`);
              const highlighted = isHighlighted(plan);
              const originalPrice = getOriginalPrice(plan);
              const displayPrice = getDisplayPrice(plan);

              return (
                <div
                  key={plan._id}
                  className="flex flex-col items-start px-5 py-6 md:p-8"
                  style={{
                    gap: '24px',
                    background: isDark ? '#0F172B' : '#FFFFFF',
                    border: `1px solid ${borderColor}`,
                    borderRadius: '16px',
                  }}
                >
                  {/* Text block: name + target + features */}
                  <div className="w-full flex flex-col" style={{ gap: '8px' }}>
                    {/* Name + Tag row */}
                    <div className="flex items-center" style={{ gap: '8px' }}>
                      <h3
                        className="font-geist flex-1"
                        style={{
                          fontWeight: 600,
                          fontSize: '24px',
                          lineHeight: '32px',
                          color: isDark ? '#E2E8F0' : '#314158',
                        }}
                      >
                        {t(`landing.pricing.${key}.name`)}
                      </h3>
                      {highlighted && (
                        <span
                          className="font-geist text-center shrink-0"
                          style={{
                            fontWeight: 500,
                            fontSize: '14px',
                            lineHeight: '20px',
                            padding: '4px 8px',
                            background: isDark ? '#3C0366' : '#FAF5FF',
                            border: `1px solid ${isDark ? '#9810FA' : '#DAB2FF'}`,
                            borderRadius: '999px',
                            color: isDark ? '#DAB2FF' : '#9810FA',
                          }}
                        >
                          {t('landing.pricing.mostPopular')}
                        </span>
                      )}
                    </div>

                    {/* Target */}
                    <p
                      className="font-geist"
                      style={{
                        fontWeight: 600,
                        fontSize: '16px',
                        lineHeight: '24px',
                        color: isDark ? '#CAD5E2' : '#45556C',
                      }}
                    >
                      {t(`landing.pricing.${key}.target`)}
                    </p>

                    {/* Feature bullets */}
                    <ul className="flex flex-col" style={{ gap: '4px' }}>
                      {features.map((feature, i) => (
                        <li
                          key={i}
                          className="font-geist"
                          style={{
                            fontWeight: 400,
                            fontSize: '16px',
                            lineHeight: '24px',
                            color: isDark ? '#CAD5E2' : '#45556C',
                            listStyleType: 'disc',
                            marginLeft: '20px',
                          }}
                        >
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Price row */}
                  <div className="flex items-center" style={{ gap: '4px' }}>
                    {originalPrice && (
                      <span
                        className="font-geist"
                        style={{
                          fontWeight: 600,
                          fontSize: '20px',
                          lineHeight: '28px',
                          textDecoration: 'line-through',
                          color: '#90A1B9',
                        }}
                      >
                        {originalPrice}
                      </span>
                    )}
                    <span className="flex items-end" style={{ gap: '2px' }}>
                      <span
                        className="font-geist"
                        style={{
                          fontWeight: 600,
                          fontSize: '30px',
                          lineHeight: '36px',
                          color: isDark ? '#E2E8F0' : '#314158',
                        }}
                      >
                        {displayPrice}
                      </span>
                      <span
                        className="font-geist"
                        style={{
                          fontWeight: 600,
                          fontSize: '14px',
                          lineHeight: '20px',
                          color: '#90A1B9',
                        }}
                      >
                        /{t('landing.pricing.perMonth')}
                      </span>
                    </span>
                  </div>

                  {/* CTA Button */}
                  <Link
                    to="/registration"
                    className="gradient-btn w-full flex items-center justify-center font-geist"
                    style={{
                      fontWeight: 500,
                      fontSize: '16px',
                      lineHeight: '24px',
                      padding: '12px 16px',
                      borderRadius: '12px',
                      ...(highlighted
                        ? {
                            background: 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)',
                            color: '#FFFFFF',
                          }
                        : {
                            background: isDark ? '#0F172B' : '#FFFFFF',
                            border: `1px solid ${isDark ? '#1D293D' : '#F1F5F9'}`,
                            color: isDark ? '#C27AFF' : '#AD46FF',
                          }),
                    }}
                  >
                    {highlighted
                      ? t('landing.pricing.ctaGradient')
                      : t('landing.pricing.ctaOutline')}
                  </Link>
                </div>
              );
            })}
          </div>

          {/* Comparison Table */}
          {comparisonRows.length > 0 && (
            <div className="w-full overflow-x-auto" style={{ borderRadius: '8px' }}>
              <table className="w-full" style={{ borderCollapse: 'collapse', minWidth: '600px' }}>
                <thead>
                  <tr>
                    <th
                      className="font-geist text-left"
                      style={{
                        fontWeight: 500,
                        fontSize: '14px',
                        lineHeight: '20px',
                        color: isDark ? '#E2E8F0' : '#314158',
                        padding: '12px 16px',
                        borderBottom: `1px solid ${borderColor}`,
                        width: '25%',
                      }}
                    >
                      {t('landing.pricing.comparison.features')}
                    </th>
                    {plans.map((plan) => {
                      const key = planKeyMap[plan.subscription_name] || plan.subscription_name.toLowerCase();
                      return (
                        <th
                          key={plan._id}
                          className="font-geist text-left"
                          style={{
                            fontWeight: 500,
                            fontSize: '14px',
                            lineHeight: '20px',
                            color: isDark ? '#E2E8F0' : '#314158',
                            padding: '12px 16px',
                            borderBottom: `1px solid ${borderColor}`,
                            width: '25%',
                          }}
                        >
                          {t(`landing.pricing.${key}.name`)}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {comparisonRows.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      <td
                        className="font-geist"
                        style={{
                          fontWeight: 400,
                          fontSize: '14px',
                          lineHeight: '20px',
                          color: isDark ? '#CAD5E2' : '#45556C',
                          padding: '12px 16px',
                          borderBottom: `1px solid ${borderColor}`,
                          verticalAlign: row.tall ? 'top' : 'middle',
                        }}
                      >
                        {t(row.labelKey)}
                      </td>
                      {row.cells.map((cell, cellIndex) => (
                        <td
                          key={cellIndex}
                          style={{
                            padding: '12px 16px',
                            borderBottom: `1px solid ${borderColor}`,
                            verticalAlign: row.tall ? 'top' : 'middle',
                          }}
                        >
                          {renderCell(cell)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
